import { encounterField } from '../../shared/array-types.js';
import { record, type Row } from '../ability/context.js';
import { stairGeometry, stairSupportAt, type RestShape } from '../inventory/floor-move.js';
import type { PlainWalkCommitInput, WalkPoint } from './plain-walk.js';
import type { PlainWalkReaders } from './plain-walk-scope.js';
import type { Point3 } from './traversal-3d.js';
import { moverMovementInt } from './difficult-terrain.js';
import { squeezeBodyFt, squeezeTier } from '../../../../ft3d/engine/ft3d-creature-size.js';

export interface StairWalkPlan { path: Point3[]; ceilingFt: number }
type StairInput = Pick<PlainWalkCommitInput, 'origin' | 'force' | 'supportChain' | 'shape' | 'stair'>;
type StairRoute = { ok: true; plan: StairWalkPlan; shape: RestShape; destination: WalkPoint; route: Point3[] }
  | { ok: false; refusal: string };

/** Diameter that fits at the apron point; independent of the stair's usable tread width. */
function apronWidth(point: readonly number[], room: RestShape['boxes'][number]): number {
  return 2 * Math.min(point[0]! - room.x.min, room.x.max - point[0]!, point[1]! - room.y.min, room.y.max - point[1]!);
}

/** Only the resting foot on a tread or its level apron owes the stair's width tier. */
export function stairSqueezeLevelAt(raw: unknown, point: WalkPoint, bodyFt: number): 0 | 1 | 2 {
  if (typeof raw === 'string') { try { raw = JSON.parse(raw); } catch { return 0; } }
  const shape = record(raw), connections = record(shape?.authoring)?.verticalConnections;
  if (!Array.isArray(shape?.boxes) || !Array.isArray(connections)) return 0;
  const boxes = shape.boxes as RestShape['boxes'];
  let level: 0 | 1 | 2 = 0;
  for (const value of connections) {
    const connection = record(value);
    if (!connection) continue;
    let stair: ReturnType<typeof stairGeometry>;
    try { stair = stairGeometry(connection, boxes); } catch { continue; }
    if (!(stair.width > 0) || stair.points.some(p => !p.every(Number.isFinite))) continue;
    for (let i = 1; i < stair.points.length; i++) {
      const a = stair.points[i - 1]!, b = stair.points[i]!;
      const dx = b[0] - a[0], dy = b[1] - a[1], length2 = dx * dx + dy * dy;
      const t = ((point.x - a[0]) * dx + (point.y - a[1]) * dy) / length2;
      // SQL rounds the accepted endpoint to thousandths of a foot.
      if (t < -.002 / Math.sqrt(length2) || t > 1 + .002 / Math.sqrt(length2)
        || Math.hypot(point.x - a[0] - t * dx, point.y - a[1] - t * dy) > stair.width / 2 + .002
        || Math.abs(point.z - a[2] - t * (b[2] - a[2])) > .002) continue;
      const room = i === 1 ? boxes.find(b => b.id === connection.sourceRoomId)
        : i === 3 ? boxes.find(b => b.id === connection.targetRoomId) : null;
      const tier = squeezeTier(bodyFt, room ? Math.min(stair.width, apronWidth([point.x, point.y], room)) : stair.width);
      // A deliberate standee placement below the two-category floor keeps the existing Very Squeezed treatment.
      const next = tier === 'normal' ? 0 : tier === 'squeezed' ? 1 : 2;
      level = Math.max(level, next) as 0 | 1 | 2;
    }
  }
  return level;
}

function closestOnPath(path: readonly Point3[], origin: Point3): { point: Point3; distance: number; index: number } | null {
  let best: ReturnType<typeof closestOnPath> = null;
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1]!, b = path[i]!, v = b.map((n, j) => n - a[j]!) as Point3;
    const length2 = v.reduce((n, x) => n + x * x, 0);
    if (length2 < 1e-12) continue;
    const t = Math.max(0, Math.min(1, v.reduce((n, x, j) => n + x * (origin[j]! - a[j]!), 0) / length2));
    const point = a.map((x, j) => x + t * v[j]!) as Point3;
    const distance = Math.hypot(...point.map((x, j) => x - origin[j]!));
    if (!best || distance < best.distance) best = { point, distance, index: i - 1 };
  }
  return best;
}

/** Partial stops retain physical stair support, including DECIMAL(…,3) coordinate rounding. */
export function stairSupportsPoint(plan: StairWalkPlan, point: WalkPoint): boolean {
  return (closestOnPath(plan.path, [point.x, point.y, point.z])?.distance ?? Infinity) <= .002;
}

/** Recognize a received walking route on native stair support without adding a second wire contract. */
export function isStairWalkPath(shape: RestShape | null | undefined, path: readonly WalkPoint[]): boolean {
  if (!shape) return false;
  let onStair = false;
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1]!, b = path[i]!;
    if (Math.abs(a.z - b.z) <= .001) continue;
    if (Math.hypot(a.x - b.x, a.y - b.y) <= 1e-6) return false;
    const midpoint = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, z: (a.z + b.z) / 2 };
    const support = stairSupportAt(shape, midpoint.x, midpoint.y, midpoint.z);
    if (support === null || Math.abs(support - midpoint.z) > .002) return false;
    onStair = true;
  }
  return onStair;
}

/** ft3d_authored_stair_walk (85565f904^): geometry and short approach once, then the shared traversal owns price/consequences. */
export function prepareStairWalkRoute(input: StairInput, encounter: Row, mover: Row, read: PlainWalkReaders): StairRoute {
  const no = (refusal: string): StairRoute => ({ ok: false, refusal });
  if (!input.stair || !['up', 'down'].includes(input.stair.direction) || input.force || input.supportChain.length)
    return no('movement:not-ported:stairs:request');
  let raw = input.shape ?? encounterField(encounter, 'encounter_shape');
  if (typeof raw === 'string') { try { raw = JSON.parse(raw); } catch { return no('movement:not-ported:stairs:shape'); } }
  const shape = record(raw), authoring = record(shape?.authoring);
  if (!shape || !Array.isArray(shape.boxes) || !Array.isArray(authoring?.verticalConnections)) return no('movement:not-ported:stairs:shape');
  const connection = authoring.verticalConnections.map(record).find(c => c?.id === input.stair!.id);
  if (!connection) return no('These stairs no longer exist.');
  const body = read.geometry(mover);
  if (!body) return no('movement:not-ported:stairs:mover-geometry');
  const size = body.rulesDiameter ?? body.diameter;
  // Owner 2026-10-27: the size ladder is a STANDEE allowance ("they become very squeezed as a standee"). A physical-mesh body
  // (rulesDiameter set) keeps the rigid width and apron checks and reads no Compression, like every other mesh collision.
  const standee = body.rulesDiameter === undefined;
  const squeezeSize = standee ? squeezeBodyFt(size, moverMovementInt(mover, read, 'squeeze_size_category_offset')) : size;
  let stair: ReturnType<typeof stairGeometry>;
  try { stair = stairGeometry(connection, shape.boxes as RestShape['boxes']); }
  catch { return no('movement:not-ported:stairs:geometry'); }
  if (!(stair.width > 0) || stair.points.some(p => !p.every(Number.isFinite))) return no('movement:not-ported:stairs:geometry');
  if (standee ? squeezeTier(squeezeSize, stair.width) === 'blocked' : size > stair.width + .001)
    return no('This creature is too wide for these stairs.');
  const source = (shape.boxes as RestShape['boxes']).find(b => b.id === connection.sourceRoomId)!;
  const target = (shape.boxes as RestShape['boxes']).find(b => b.id === connection.targetRoomId)!;
  if (!(target.floor > source.floor)) return no('movement:not-ported:stairs:floor-order');
  for (const [point, room] of [[stair.points[0]!, source], [stair.points[3]!, target]] as const) {
    if (standee ? squeezeTier(squeezeSize, apronWidth(point, room)) === 'blocked'
      : point[0] - size / 2 < room.x.min - .001 || point[0] + size / 2 > room.x.max + .001
        || point[1] - size / 2 < room.y.min - .001 || point[1] + size / 2 > room.y.max + .001)
      return no('The stair landing is too small for this creature.');
  }
  const points = input.stair.direction === 'up' ? stair.points : stair.points.slice().reverse();
  const origin = read.position(mover);
  const best = closestOnPath(points, origin);
  if (!best || best.distance > 3 || Math.abs(best.point[2] - origin[2]) > .3) return no('Move to the stair landing first.');
  const passageSize = standee ? squeezeBodyFt(squeezeSize, 2) : size;
  if (best.index === 1 && best.distance > Math.max(.05, (stair.width - passageSize) / 2)) return no('Move onto the stair landing first.');
  const path = [origin, best.point, ...points.slice(best.index + 1)]
    .filter((p, i, all) => i === 0 || Math.hypot(...p.map((x, j) => x - all[i - 1]![j]!)) > 1e-9);
  if (path.length < 2) return no('You are already at that stair landing.');
  const end = path[path.length - 1]!;
  // A private route corridor opens only the source ceiling. Held architecture, walls/doors and upper bands stay intact.
  const corridor = { ...shape, boxes: (shape.boxes as RestShape['boxes']).map(b => b === source ? { ...b, ceiling: target.ceiling } : b) } as RestShape;
  return { ok: true, plan: { path, ceilingFt: target.ceiling }, shape: corridor,
    destination: { x: end[0], y: end[1], z: end[2] }, route: path };
}
